import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Filter, ChevronLeft, ChevronRight, LayoutGrid, TableIcon } from 'lucide-react';
import { formatDate } from './utils';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

export const FiltersBar = ({
  canManageTimeTracking,
  branches,
  locations,
  employees,
  selectedBranch,
  setSelectedBranch,
  selectedLocation,
  setSelectedLocation,
  selectedEmployee,
  setSelectedEmployee,
  viewMode,
  changeViewMode,
  displayMode,
  setDisplayMode,
  navigatePeriod,
  dateRange,
}) => (
  <Card className="border-border/60">
    <CardContent className="pt-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filter:</span>
        </div>

        <Select value={selectedBranch} onValueChange={(v) => {
          setSelectedBranch(v);
          setSelectedLocation('all');
          setSelectedEmployee('all');
        }}>
          <SelectTrigger className={`w-[200px] ${canManageTimeTracking ? '' : 'hidden'}`} data-testid="filter-branch">
            <SelectValue placeholder="Niederlassung" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Niederlassungen</SelectItem>
            {branches.map(b => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedLocation} onValueChange={(v) => {
          setSelectedLocation(v);
          setSelectedEmployee('all');
        }}>
          <SelectTrigger className={`w-[200px] ${canManageTimeTracking ? '' : 'hidden'}`} data-testid="filter-location">
            <SelectValue placeholder="Standort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Standorte</SelectItem>
            {locations
              .filter(l => selectedBranch === 'all' || l.branch_id === selectedBranch)
              .map(l => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
          <SelectTrigger className="w-[200px]" data-testid="filter-employee">
            <SelectValue placeholder="Mitarbeiter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Mitarbeiter</SelectItem>
            {(() => {
              const dk = getDuplicateNameKeys(employees);
              return employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>
                  {formatLastFirst(emp, dk)}
                </SelectItem>
              ));
            })()}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 border rounded-lg p-1">
          <Button
            variant={viewMode === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => changeViewMode('week')}
          >
            Woche
          </Button>
          <Button
            variant={viewMode === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => changeViewMode('month')}
          >
            Monat
          </Button>
        </div>

        <div className="flex items-center gap-1 border rounded-lg p-1">
          <Button
            variant={displayMode === 'table' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setDisplayMode('table')}
            data-testid="view-table-btn"
          >
            <TableIcon className="h-4 w-4 mr-1" />Tabelle
          </Button>
          <Button
            variant={displayMode === 'cards' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setDisplayMode('cards')}
            data-testid="view-cards-btn"
          >
            <LayoutGrid className="h-4 w-4 mr-1" />Karten
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigatePeriod(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium px-2 min-w-[180px] text-center">
            {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
          </span>
          <Button variant="outline" size="icon" onClick={() => navigatePeriod(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);
